// Composant barre de progression avec seuils pour les casques — V7
// 5 zones (1-5 casques), interface SeuilCasque[]
// Flex layout — no absolute positioning, minimum text-xs

import type { SeuilCasque } from '@autodiag/shared'

interface ScoreProgressBarProps {
  score: number
  seuils: SeuilCasque[] // 5 éléments [{min,max}, ...]
  compact?: boolean
  animated?: boolean
  showCursor?: boolean
}

export default function ScoreProgressBar({
  score,
  seuils,
  compact = false,
  animated = true,
  showCursor = true,
}: ScoreProgressBarProps) {
  // Max affiché = seuil du 5e casque * 1.1
  const maxScore = seuils[4].min * 1.1
  const progressPct = Math.min((score / maxScore) * 100, 100)

  // Pourcentages de chaque seuil
  const seuil1Pct = (seuils[1].min / maxScore) * 100
  const seuil2Pct = (seuils[2].min / maxScore) * 100
  const seuil3Pct = (seuils[3].min / maxScore) * 100
  const seuil4Pct = (seuils[4].min / maxScore) * 100

  const barHeight = compact ? 'h-4' : 'h-6'
  const cursorSize = compact
    ? 'border-l-[6px] border-r-[6px] border-t-[8px]'
    : 'border-l-[8px] border-r-[8px] border-t-[10px]'
  const cursorTop = compact ? '-top-2' : '-top-3'

  // Zone widths as flex-basis percentages
  const zone0Width = seuil1Pct
  const zone1Width = seuil2Pct - seuil1Pct
  const zone2Width = seuil3Pct - seuil2Pct
  const zone3Width = seuil4Pct - seuil3Pct
  const zone4Width = 100 - seuil4Pct

  return (
    <div>
      {/* Barre de progression */}
      <div className={`relative ${barHeight} bg-muted rounded-full overflow-visible`}>
        <div className="absolute inset-0 rounded-full bg-muted" />

        {/* Remplissage de progression */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full overflow-hidden ${animated ? 'transition-all duration-700' : ''}`}
          style={{ width: `${progressPct}%` }}
        >
          <div className="h-full flex">
            <div
              className="bg-primary/20 h-full"
              style={{
                width: `${(seuil1Pct / Math.max(progressPct, 1)) * 100}%`,
                minWidth: progressPct > 0 && score < seuils[1].min ? '100%' : undefined,
              }}
            />
            {score >= seuils[1].min && (
              <div
                className="bg-primary/40 h-full"
                style={{
                  width: `${((seuil2Pct - seuil1Pct) / Math.max(progressPct, 1)) * 100}%`,
                  minWidth: score < seuils[2].min ? '100%' : undefined,
                }}
              />
            )}
            {score >= seuils[2].min && (
              <div
                className="bg-primary/60 h-full"
                style={{
                  width: `${((seuil3Pct - seuil2Pct) / Math.max(progressPct, 1)) * 100}%`,
                  minWidth: score < seuils[3].min ? '100%' : undefined,
                }}
              />
            )}
            {score >= seuils[3].min && (
              <div
                className="bg-primary/80 h-full"
                style={{
                  width: `${((seuil4Pct - seuil3Pct) / Math.max(progressPct, 1)) * 100}%`,
                  minWidth: score < seuils[4].min ? '100%' : undefined,
                }}
              />
            )}
            {score >= seuils[4].min && <div className="bg-primary h-full flex-1" />}
          </div>
        </div>

        {/* Séparateurs verticaux — 4 séparateurs pour 5 zones */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-background" style={{ left: `${seuil1Pct}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-background" style={{ left: `${seuil2Pct}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-background" style={{ left: `${seuil3Pct}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-background" style={{ left: `${seuil4Pct}%` }} />

        {/* Curseur triangulaire */}
        {showCursor && (
          <div
            className={`absolute ${cursorTop} ${animated ? 'transition-all duration-700' : ''}`}
            style={{ left: `${progressPct}%`, transform: 'translateX(-50%)' }}
          >
            <div className={`w-0 h-0 border-l-transparent border-r-transparent border-t-foreground ${cursorSize}`} />
          </div>
        )}
      </div>

      {/* Labels — flex layout instead of absolute positioning */}
      <div className="flex mt-2">
        <div className="text-center" style={{ flexBasis: `${zone0Width}%` }}>
          <div className="text-xs font-medium text-primary/40 truncate">
            1 {!compact && 'casque'}
          </div>
          <div className="text-xs text-muted-foreground">
            &lt; {seuils[1].min}{!compact && ' pts'}
          </div>
        </div>
        <div className="text-center" style={{ flexBasis: `${zone1Width}%` }}>
          <div className="text-xs font-medium text-primary/60 truncate">
            2 {!compact && 'casques'}
          </div>
          <div className="text-xs text-muted-foreground">
            {seuils[1].min}-{seuils[2].min - 1}{!compact && ' pts'}
          </div>
        </div>
        <div className="text-center" style={{ flexBasis: `${zone2Width}%` }}>
          <div className="text-xs font-medium text-primary/80 truncate">
            3 {!compact && 'casques'}
          </div>
          <div className="text-xs text-muted-foreground">
            {seuils[2].min}-{seuils[3].min - 1}{!compact && ' pts'}
          </div>
        </div>
        <div className="text-center" style={{ flexBasis: `${zone3Width}%` }}>
          <div className="text-xs font-medium text-primary/90 truncate">
            4 {!compact && 'casques'}
          </div>
          <div className="text-xs text-muted-foreground">
            {seuils[3].min}-{seuils[4].min - 1}{!compact && ' pts'}
          </div>
        </div>
        <div className="text-center" style={{ flexBasis: `${zone4Width}%` }}>
          <div className="text-xs font-medium text-primary truncate">
            5 {!compact && 'casques'}
          </div>
          <div className="text-xs text-muted-foreground">
            &gt; {seuils[4].min - 1}{!compact && ' pts'}
          </div>
        </div>
      </div>
    </div>
  )
}
